import { useCallback, useRef } from 'react';
import { ResourceType } from 'libs.library_manager';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { projectDocumentToMomFields } from '../utils/consent_element_projector';
import { validateConsentDocumentForPublish } from '../utils/consent_publish_validation';
const RESOURCE_TYPE = ResourceType.CONSENT_TEMPLATE;
const ASSET_RESOURCE_TYPE = ResourceType.CONSENT_ASSET;
const PUBLISHED_TEMPLATE_POLL_INTERVAL_MS = 250;
const PUBLISHED_TEMPLATE_POLL_ATTEMPTS = 12;
const libraryStatusToDisplay = (status) => {
    switch (status) {
        case 'PUBLISHED':
            return 'Published';
        case 'IN_REVIEW':
            return 'In Review';
        case 'ARCHIVED':
            return 'Archived';
        default:
            return 'Draft';
    }
};
const resourceData = (resource) => (resource.data ?? {});
const resourceToFileRow = (resource) => {
    const data = resourceData(resource);
    return {
        id: resource.id,
        templateKey: resource.key,
        title: resource.name,
        version: resource.version,
        language: data.language ?? 'en_US',
        description: data.description ?? '',
        sigFields: data.sigFields ?? '—',
        sites: data.sites ?? '—',
        status: libraryStatusToDisplay(resource.status),
    };
};
const resourceToDocument = (resource) => {
    const data = resourceData(resource);
    const meta = (resource.metadata ?? {});
    return {
        id: resource.id,
        key: resource.key,
        name: resource.name,
        status: libraryStatusToDisplay(resource.status),
        assetId: meta.consentAssetId ?? '',
        pdfDataUrl: '',
        totalPages: data.totalPages ?? 0,
        elements: data.elements ?? [],
        roles: data.roles ?? [],
        createdAt: resource.createdAt,
        updatedAt: resource.updatedAt,
    };
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const useConsentLibrary = (entityId, studyId) => {
    const { get, post, put } = useApiClient(ApiClientService.LIBRARY);
    const { post: postMom } = useApiClient(ApiClientService.MOM);
    const getRef = useRef(get);
    getRef.current = get;
    const postRef = useRef(post);
    postRef.current = post;
    const putRef = useRef(put);
    putRef.current = put;
    const postMomRef = useRef(postMom);
    postMomRef.current = postMom;
    const loadResources = useCallback(async () => {
        if (!entityId || !studyId) {
            return { fileRows: [], documents: [] };
        }
        const { data } = await getRef.current(`v1/${RESOURCE_TYPE}`, {
            params: { entityId, studyId, active: true },
        });
        const resources = data.data;
        return {
            fileRows: resources.map(resourceToFileRow),
            documents: resources.map(resourceToDocument),
        };
    }, [entityId, studyId]);
    const waitForPublishedTemplateVisibility = useCallback(async (resourceId, key) => {
        for (let attempt = 0; attempt < PUBLISHED_TEMPLATE_POLL_ATTEMPTS; attempt += 1) {
            const { data } = await getRef.current(`v1/${RESOURCE_TYPE}`, {
                params: { key, status: 'PUBLISHED', latest: true },
            });
            if (data.data[0]?.id === resourceId) {
                return;
            }
            await sleep(PUBLISHED_TEMPLATE_POLL_INTERVAL_MS);
        }
        throw new Error(`Published consent template ${resourceId} did not become visible as latest published in time.`);
    }, []);
    const resyncTemplateInMom = useCallback(async (key, triggerReason) => {
        if (!entityId || !studyId) {
            return;
        }
        await postMomRef.current(`/v1/entities/${entityId}/studies/${studyId}/econsent/templates/${key}/resync`, {
            triggerReason,
        });
    }, [entityId, studyId]);
    const createResource = useCallback(async (file, name, initialData) => {
        // Step 1: upload the PDF as a CONSENT_ASSET
        const assetFormData = new FormData();
        assetFormData.append('file', file, file.name);
        assetFormData.append('name', name);
        assetFormData.append('type', ASSET_RESOURCE_TYPE);
        assetFormData.append('status', 'PUBLISHED');
        if (entityId) {
            assetFormData.append('entityId', entityId.toString());
        }
        if (studyId) {
            assetFormData.append('studyId', studyId);
        }
        assetFormData.append('data', JSON.stringify({ fileName: file.name, mimeType: file.type || 'application/pdf' }));
        const { data: assetResponse } = await postRef.current(`v1/${ASSET_RESOURCE_TYPE}`, assetFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const asset = assetResponse.data;
        // Step 2: create the CONSENT_TEMPLATE referencing the asset
        const { data: templateResponse } = await postRef.current(`v1/${RESOURCE_TYPE}`, {
            name,
            type: RESOURCE_TYPE,
            status: 'DRAFT',
            ...(entityId ? { entityId: entityId.toString() } : {}),
            ...(studyId ? { studyId } : {}),
            data: {
                elements: [],
                totalPages: 0,
                roles: [],
                ...projectDocumentToMomFields([], []),
                language: initialData.language ?? 'en_US',
                sites: initialData.sites ?? '—',
                description: initialData.description ?? '—',
                sigFields: '—',
            },
            metadata: {
                consentAssetId: asset.id,
            },
        });
        const resource = templateResponse.data;
        return {
            fileRow: resourceToFileRow(resource),
            document: resourceToDocument(resource),
        };
    }, [entityId, studyId]);
    const updateResourceData = useCallback(async (id, dataPayload) => {
        const momFields = dataPayload.elements !== undefined
            ? projectDocumentToMomFields(dataPayload.elements, dataPayload.roles ?? [])
            : {};
        await putRef.current(`v1/${RESOURCE_TYPE}/${id}`, { data: { ...dataPayload, ...momFields } });
    }, []);
    const archiveResource = useCallback(async (id, key) => {
        await putRef.current(`v1/${RESOURCE_TYPE}/${id}`, { status: 'ARCHIVED' });
        if (key) {
            await resyncTemplateInMom(key, 'archive');
        }
    }, [resyncTemplateInMom]);
    const getResourcePdfUrl = useCallback(async (assetId) => {
        const { data } = await getRef.current(`v1/${ASSET_RESOURCE_TYPE}/${assetId}`, {
            params: { withUrl: true },
        });
        return data.data.blobUrl ?? '';
    }, []);
    const saveCompiledTemplate = useCallback(async (id, name, zipBlob, bundle, documentData) => {
        const formData = new FormData();
        formData.append('file', zipBlob, `${name}_consent_content.zip`);
        formData.append('data', JSON.stringify({
            templatePackageId: bundle.templatePackageId,
            pdfFileName: bundle.pdfFileName,
            elements: documentData.elements,
            totalPages: documentData.totalPages,
            roles: documentData.roles,
            ...projectDocumentToMomFields(documentData.elements, documentData.roles),
        }));
        await putRef.current(`v1/${RESOURCE_TYPE}/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }, []);
    const publishTemplate = useCallback(async (document) => {
        const validationError = validateConsentDocumentForPublish(document);
        if (validationError) {
            throw new Error(validationError);
        }
        // HACK: The library API does not permit a direct DRAFT → PUBLISHED transition.
        // Permitted transitions from DRAFT are IN_REVIEW and ARCHIVED; from IN_REVIEW
        // the transition to PUBLISHED is valid. We go DRAFT → IN_REVIEW → PUBLISHED
        // as a workaround until the business requirements for IN_REVIEW are finalised.
        await putRef.current(`v1/${RESOURCE_TYPE}/${document.id}`, { status: 'IN_REVIEW' });
        await putRef.current(`v1/${RESOURCE_TYPE}/${document.id}`, { status: 'PUBLISHED' });
        if (entityId && studyId) {
            // Temporary hack: poll the same "latest published by key" lookup MOM uses until the
            // newly published resource is visible there, then trigger MOM resync.
            await waitForPublishedTemplateVisibility(document.id, document.key);
            await resyncTemplateInMom(document.key, 'publish');
        }
    }, [entityId, studyId, waitForPublishedTemplateVisibility, resyncTemplateInMom]);
    return {
        loadResources,
        createResource,
        updateResourceData,
        archiveResource,
        getResourcePdfUrl,
        saveCompiledTemplate,
        publishTemplate,
    };
};
